import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { invoiceService } from '../services/invoiceService';
import {
  Invoice,
  InvoiceStatus,
  INVOICE_STATUS_OPTIONS,
  MONTH_OPTIONS,
  getMonthName,
  formatInvoiceAmount,
  ClientWithPlacements,
  ProjectWithPlacements,
} from '../types/invoice';
import SettingsBackButton from '../components/common/SettingsBackButton';

interface InvoicesPageState {
  selectedMonth: number;
  selectedYear: number;
  selectedClient: string;
  selectedProject: string;
  selectedStatus: InvoiceStatus | '';
}

// Combined row type for table
type TableRow = {
  type: 'client' | 'invoice';
  client?: ClientWithPlacements;
  invoice?: Invoice;
};

export default function InvoicesPage() {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();
  const canEditInvoices = isSuperAdmin;
  const currentDate = new Date();
  
  const [state, setState] = useState<InvoicesPageState>({
    selectedMonth: currentDate.getMonth() + 1,
    selectedYear: currentDate.getFullYear(),
    selectedClient: '',
    selectedProject: '',
    selectedStatus: '',
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshing, setRefreshing] = useState(false);

  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientWithPlacements | null>(null);
  const [overrideAmount, setOverrideAmount] = useState<string>('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemPrice, setEditingItemPrice] = useState<string>('');
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  // Generate year options (current year and 2 years back)
  const yearOptions = Array.from({ length: 3 }, (_, i) => currentDate.getFullYear() - i);

  // Fetch clients with completed placements for selected month
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients-with-placements', state.selectedMonth, state.selectedYear],
    queryFn: () => invoiceService.getClientsWithCompletedPlacements(state.selectedMonth, state.selectedYear),
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache (replaces deprecated cacheTime)
  });

  const clients = (clientsData as any)?.data || [];

  const { data: projectsData } = useQuery({
    queryKey: ['projects-with-placements', state.selectedClient, state.selectedMonth, state.selectedYear],
    queryFn: () =>
      invoiceService.getProjectsWithCompletedPlacements(
        state.selectedClient,
        state.selectedMonth,
        state.selectedYear
      ),
    enabled: Boolean(state.selectedClient),
    staleTime: 0,
    gcTime: 0,
  });

  const projects: ProjectWithPlacements[] = projectsData?.data || [];

  // Fetch invoices with filters
  const { data: invoicesData, isLoading: invoicesLoading, refetch } = useQuery({
    queryKey: ['invoices', state.selectedClient, state.selectedProject, state.selectedMonth, state.selectedYear, state.selectedStatus],
    queryFn: () => invoiceService.getInvoices({
      clientId: state.selectedClient || undefined,
      projectId: state.selectedProject || undefined,
      month: state.selectedMonth,
      year: state.selectedYear,
      status: state.selectedStatus || undefined,
    }),
  });

  const invoices = invoicesData?.data || [];

  const tableRows: TableRow[] = useMemo(
    () => invoices.map((invoice) => ({ type: 'invoice' as const, invoice })),
    [invoices]
  );

  // Regenerate invoice mutation
  const regenerateMutation = useMutation({
    mutationFn: (id: string) => invoiceService.regenerateInvoice(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['clients-with-placements'] });
      setSnackbar({
        open: true,
        message: response.message || 'Invoice regenerated successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to regenerate invoice',
        severity: 'error',
      });
    },
  });

  // Override amount mutation
  const overrideMutation = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: number }) =>
      invoiceService.overrideAmount(id, { overrideAmount: amount }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setOverrideDialogOpen(false);
      setSelectedInvoice(null);
      setOverrideAmount('');
      setSnackbar({
        open: true,
        message: response.message || 'Invoice amount updated successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update invoice amount',
        severity: 'error',
      });
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: InvoiceStatus }) =>
      invoiceService.updateStatus(id, { status }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setSnackbar({
        open: true,
        message: response.message || 'Invoice status updated successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update invoice status',
        severity: 'error',
      });
    },
  });

  // Delete invoice mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => invoiceService.deleteInvoice(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['clients-with-placements'] });
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
      setSnackbar({
        open: true,
        message: response.message || 'Invoice deleted successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete invoice',
        severity: 'error',
      });
    },
  });

  // Update invoice item price mutation
  const updateItemPriceMutation = useMutation({
    mutationFn: ({ invoiceId, itemId, unitPrice }: { invoiceId: string; itemId: string; unitPrice: number }) =>
      invoiceService.updateInvoiceItemPrice(invoiceId, itemId, unitPrice),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setEditingItemId(null);
      setEditingItemPrice('');
      // Update the selected invoice with the new data
      if (selectedInvoice && response.data) {
        setSelectedInvoice(response.data);
      }
      setSnackbar({
        open: true,
        message: 'Invoice item price updated successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update item price',
        severity: 'error',
      });
    },
  });

  const handleOpenOverrideDialog = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setOverrideAmount(invoice.overrideAmount?.toString() || invoice.totalAmount.toString());
    setOverrideDialogOpen(true);
  };

  const handleSaveOverride = () => {
    if (selectedInvoice && overrideAmount) {
      const amount = parseFloat(overrideAmount);
      if (!isNaN(amount) && amount >= 0) {
        overrideMutation.mutate({ id: selectedInvoice.id, amount });
      }
    }
  };

  const handleStatusChange = (invoice: Invoice, newStatus: InvoiceStatus) => {
    updateStatusMutation.mutate({ id: invoice.id, status: newStatus });
  };

  const handleOpenDeleteDialog = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDeleteDialogOpen(true);
  };

  const handleDeleteInvoice = () => {
    if (selectedInvoice) {
      deleteMutation.mutate(selectedInvoice.id);
    }
  };

  const handleOpenDetailsDialog = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDetailsDialogOpen(true);
  };

  const handleStartEditItem = (itemId: string, currentPrice: number) => {
    setEditingItemId(itemId);
    setEditingItemPrice(currentPrice.toString());
  };

  const handleCancelEditItem = () => {
    setEditingItemId(null);
    setEditingItemPrice('');
  };

  const handleSaveItemPrice = (itemId: string) => {
    if (selectedInvoice && editingItemPrice) {
      const unitPrice = parseFloat(editingItemPrice);
      if (!isNaN(unitPrice) && unitPrice >= 0) {
        updateItemPriceMutation.mutate({
          invoiceId: selectedInvoice.id,
          itemId,
          unitPrice,
        });
      }
    }
  };

  const handleExportCSV = () => {
    const csvData = invoices.map(inv => ({
      'Invoice #': inv.invoiceNumber,
      'Client': inv.client?.name || '',
      'Period': `${getMonthName(inv.month)} ${inv.year}`,
      'Placements': inv.items?.length || 0,
      'Total Amount': inv.totalAmount,
      'Override': inv.overrideAmount || '',
      'Final Amount': inv.finalAmount,
      'Status': inv.status,
      'Generated': new Date(inv.generatedAt).toLocaleDateString(),
    }));

    const headers = Object.keys(csvData[0] || {});
    const csv = [
      headers.join(','),
      ...csvData.map(row => headers.map(h => row[h as keyof typeof row]).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    setSnackbar({
      open: true,
      message: 'Invoices exported successfully',
      severity: 'success',
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setTimeout(() => setRefreshing(false), 600);
  };

  const handleDownloadPDF = (invoice: Invoice) => {
    // Generate PDF content
    const pdfContent = generatePDFContent(invoice);
    
    // Create a new window with the invoice content
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print dialog
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };

      setSnackbar({
        open: true,
        message: 'Opening print dialog for PDF download',
        severity: 'success',
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Please allow popups to download invoice',
        severity: 'error',
      });
    }
  };

  const generatePDFContent = (invoice: Invoice): string => {
    const items = invoice.items || [];
    const itemsHTML = items.map((item, index) => `
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${index + 1}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${item.description}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${item.live_url ? `<a href="${item.live_url}" target="_blank" style="color: #1976d2; text-decoration: none;">${item.live_url}</a>` : '-'}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">${formatInvoiceAmount(item.amount)}</td>
      </tr>
    `).join('');

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${invoice.invoiceNumber}</title>
  <style>
    @media print {
      body { margin: 0; }
      @page { margin: 20mm; }
    }
    body { 
      font-family: Arial, sans-serif; 
      margin: 40px;
      max-width: 800px;
      margin-left: auto;
      margin-right: auto;
    }
    .header { 
      text-align: center; 
      margin-bottom: 30px;
      border-bottom: 3px solid #333;
      padding-bottom: 20px;
    }
    .header h1 {
      margin: 0;
      color: #333;
      font-size: 32px;
    }
    .header h2 {
      margin: 10px 0 0 0;
      color: #666;
      font-size: 24px;
    }
    .invoice-details { 
      margin-bottom: 30px;
      background-color: #f9f9f9;
      padding: 15px;
      border-radius: 5px;
    }
    .invoice-details table { width: 100%; }
    .invoice-details td { padding: 8px; }
    .items-table { 
      width: 100%; 
      border-collapse: collapse; 
      margin-bottom: 30px;
      border: 1px solid #ddd;
    }
    .items-table th { 
      background-color: #333; 
      color: white;
      padding: 12px; 
      text-align: left; 
      font-weight: bold;
    }
    .items-table td { 
      padding: 10px; 
      border-bottom: 1px solid #ddd; 
    }
    .items-table tbody tr:last-child td {
      border-bottom: none;
    }
    .totals { 
      text-align: right; 
      margin-top: 30px;
      padding-top: 20px;
      border-top: 2px solid #333;
    }
    .totals table { 
      margin-left: auto;
      min-width: 300px;
    }
    .totals td { 
      padding: 10px 15px;
      font-size: 18px;
    }
    .total-row { 
      font-weight: bold; 
      font-size: 1.3em;
      background-color: #f0f0f0;
      border-top: 2px solid #333;
    }
    .footer {
      margin-top: 50px;
      padding-top: 20px;
      border-top: 1px solid #ddd;
      text-align: center;
      color: #666;
      font-size: 14px;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>INVOICE</h1>
    <h2>${invoice.invoiceNumber}</h2>
  </div>

  <div class="invoice-details">
    <table>
      <tr>
        <td><strong>Bill To:</strong></td>
        <td><strong>Invoice Date:</strong></td>
      </tr>
      <tr>
        <td>${invoice.client?.name || 'N/A'}</td>
        <td>${new Date(invoice.generatedAt).toLocaleDateString()}</td>
      </tr>
      <tr>
        <td>${invoice.client?.email || ''}</td>
        <td><strong>Period:</strong> ${getMonthName(invoice.month)} ${invoice.year}</td>
      </tr>
      <tr>
        <td></td>
        <td><strong>Status:</strong> ${invoice.status}</td>
      </tr>
    </table>
  </div>

  <table class="items-table">
    <thead>
      <tr>
        <th>#</th>
        <th>Description</th>
        <th>Live URL</th>
        <th style="text-align: right;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${itemsHTML}
    </tbody>
  </table>

  <div class="totals">
    <table>
      <tr>
        <td>Subtotal:</td>
        <td style="text-align: right;">${formatInvoiceAmount(invoice.totalAmount)}</td>
      </tr>
      ${invoice.overrideAmount && invoice.overrideAmount !== invoice.totalAmount ? `
      <tr>
        <td>Adjustment:</td>
        <td style="text-align: right;">${formatInvoiceAmount(invoice.overrideAmount - invoice.totalAmount)}</td>
      </tr>
      ` : ''}
      <tr class="total-row">
        <td>Total:</td>
        <td style="text-align: right;">${formatInvoiceAmount(invoice.finalAmount)}</td>
      </tr>
    </table>
  </div>

  <div class="footer">
    <p>Thank you for your business!</p>
  </div>
</body>
</html>
    `;
  };

  const getStatusColor = (status: InvoiceStatus) => {
    const option = INVOICE_STATUS_OPTIONS.find(opt => opt.value === status);
    return option?.color || 'default';
  };

  const getStatusClass = (status: InvoiceStatus) => {
    switch(status) {
      case 'DRAFT': return 'ss-draft';
      case 'PAID': return 'ss-paid';
      case 'SENT': return 'ss-pending';
      case 'OVERDUE': return 'ss-overdue';
      default: return 'ss-draft';
    }
  };

  const getStatusPillClass = (status: InvoiceStatus) => {
    switch(status) {
      case 'DRAFT': return 'sp-draft';
      case 'PAID': return 'sp-paid';
      case 'SENT': return 'sp-pending';
      case 'OVERDUE': return 'sp-overdue';
      default: return 'sp-draft';
    }
  };

  // Filter table rows by search query
  const filteredRows = useMemo(() => {
    return tableRows.filter(row => {
      const query = searchQuery.toLowerCase();
      if (row.type === 'invoice' && row.invoice) {
        return (
          row.invoice.invoiceNumber.toLowerCase().includes(query) ||
          row.invoice.client?.name.toLowerCase().includes(query) ||
          row.invoice.client?.email.toLowerCase().includes(query)
        );
      } else if (row.type === 'client' && row.client) {
        return (
          row.client.name.toLowerCase().includes(query) ||
          row.client.email.toLowerCase().includes(query)
        );
      }
      return false;
    });
  }, [tableRows, searchQuery]);

  // Calculate stats from invoices only
  const stats = useMemo(() => {
    const total = invoices.reduce((sum, inv) => sum + inv.finalAmount, 0);
    const paid = invoices.filter(inv => inv.status === 'PAID').reduce((sum, inv) => sum + inv.finalAmount, 0);
    const pending = invoices.filter(inv => inv.status === 'SENT').reduce((sum, inv) => sum + inv.finalAmount, 0);
    const overdue = invoices.filter(inv => inv.status === 'OVERDUE').reduce((sum, inv) => sum + inv.finalAmount, 0);
    return { total, paid, pending, overdue };
  }, [invoices]);

  // Pagination for table rows
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  return (
    <>
      <style>{`
        :root {
          --bg: #F4F5F7;
          --surface: #FFFFFF;
          --surface-2: #F9FAFB;
          --surface-3: #FAFAFA;
          --border: #E5E7EB;
          --border-sub: #F3F4F6;
          --border-strong: #D1D5DB;
          --t1: #111827;
          --t2: #374151;
          --t3: #6B7280;
          --t4: #9CA3AF;
          --t5: #D1D5DB;
          --blue: #4F46E5;
          --blue-h: #4338CA;
          --blue-l: #EEF2FF;
          --blue-b: #C7D2FE;
          --green: #059669;
          --green-h: #047857;
          --green-l: #ECFDF5;
          --green-b: #A7F3D0;
          --amber: #D97706;
          --amber-h: #B45309;
          --amber-l: #FFFBEB;
          --amber-b: #FDE68A;
          --red: #DC2626;
          --red-h: #B91C1C;
          --red-l: #FEF2F2;
          --red-b: #FECACA;
          --teal: #0D9488;
          --teal-l: #F0FDFA;
          --teal-b: #99F6E4;
          --r: 6px;
          --rl: 8px;
          --rxl: 12px;
          --sh-xs: 0 1px 2px rgba(0,0,0,.05);
          --sh-sm: 0 1px 3px rgba(0,0,0,.07),0 1px 2px rgba(0,0,0,.04);
          --sh-md: 0 4px 12px rgba(0,0,0,.07),0 2px 4px rgba(0,0,0,.04);
        }

        .invoices-page {
          padding: 22px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .ph {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .ph-title {
          font-size: 19px;
          font-weight: 700;
          letter-spacing: -0.4px;
          color: var(--t1);
          margin: 0;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 13px;
          border-radius: var(--r);
          font-family: inherit;
          font-size: 12.5px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.14s;
          white-space: nowrap;
          line-height: 1;
        }

        .btn svg {
          width: 13px;
          height: 13px;
          flex-shrink: 0;
        }

        .btn-secondary {
          background: var(--surface);
          color: var(--t2);
          border: 1px solid var(--border);
          box-shadow: var(--sh-xs);
        }

        .btn-secondary:hover {
          background: var(--surface-2);
          color: var(--t1);
        }

        .qstats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .qstat {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          padding: 14px 16px;
          box-shadow: var(--sh-xs);
          display: flex;
          align-items: center;
          gap: 12px;
          transition: box-shadow 0.18s, transform 0.18s;
          cursor: default;
        }

        .qstat:hover {
          box-shadow: var(--sh-md);
          transform: translateY(-1px);
        }

        .qstat-ico {
          width: 36px;
          height: 36px;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .qstat-ico svg {
          width: 16px;
          height: 16px;
        }

        .qi-all {
          background: var(--blue-l);
        }

        .qi-all svg {
          color: var(--blue);
        }

        .qi-paid {
          background: var(--green-l);
        }

        .qi-paid svg {
          color: var(--green);
        }

        .qi-pend {
          background: var(--amber-l);
        }

        .qi-pend svg {
          color: var(--amber);
        }

        .qi-over {
          background: var(--red-l);
        }

        .qi-over svg {
          color: var(--red);
        }

        .qstat-val {
          font-size: 18px;
          font-weight: 700;
          letter-spacing: -0.4px;
          color: var(--t1);
          font-family: 'JetBrains Mono', monospace;
          line-height: 1;
        }

        .qstat-lbl {
          font-size: 11.5px;
          color: var(--t3);
          margin-top: 3px;
        }

        .filter-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          box-shadow: var(--sh-xs);
          padding: 12px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .fsearch-wrap {
          display: flex;
          align-items: center;
          gap: 7px;
          background: var(--surface-2);
          border: 1px solid var(--border);
          border-radius: var(--r);
          padding: 6px 10px;
          min-width: 200px;
          transition: all 0.15s;
        }

        .fsearch-wrap:focus-within {
          background: var(--surface);
          border-color: var(--blue);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
        }

        .fsearch-wrap svg {
          width: 13px;
          height: 13px;
          color: var(--t4);
          flex-shrink: 0;
        }

        .fsearch-wrap input {
          border: none;
          background: none;
          outline: none;
          font-family: inherit;
          font-size: 12.5px;
          color: var(--t1);
          width: 100%;
        }

        .fsearch-wrap input::placeholder {
          color: var(--t4);
        }

        .fsel {
          appearance: none;
          border: 1px solid var(--border);
          border-radius: var(--r);
          padding: 7px 28px 7px 11px;
          font-family: inherit;
          font-size: 12.5px;
          color: var(--t2);
          background: var(--surface-2);
          outline: none;
          cursor: pointer;
          transition: all 0.14s;
          min-width: 145px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 8px center;
        }

        .fsel:focus {
          border-color: var(--blue);
          background-color: var(--surface);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
        }

        .fsp {
          flex: 1;
        }

        .refresh-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 13px;
          border-radius: var(--r);
          border: 1px solid var(--border);
          background: var(--surface);
          font-family: inherit;
          font-size: 12.5px;
          font-weight: 500;
          color: var(--t2);
          cursor: pointer;
          box-shadow: var(--sh-xs);
          transition: all 0.14s;
          white-space: nowrap;
        }

        .refresh-btn:hover {
          background: var(--surface-2);
          color: var(--t1);
        }

        .refresh-btn svg {
          width: 13px;
          height: 13px;
          color: var(--blue);
        }

        .refresh-btn.spinning svg {
          animation: spin 0.6s linear 1;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .table-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          box-shadow: var(--sh-xs);
          overflow: hidden;
        }

        .table-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px;
          border-bottom: 1px solid var(--border-sub);
          background: var(--surface-3);
        }

        .tcnt {
          font-size: 12.5px;
          color: var(--t3);
        }

        .tcnt b {
          color: var(--t1);
          font-weight: 600;
        }

        .tscroll {
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          min-width: 960px;
        }

        thead th {
          text-align: left;
          font-size: 10.5px;
          font-weight: 600;
          color: var(--t4);
          letter-spacing: 0.5px;
          text-transform: uppercase;
          padding: 10px 14px;
          border-bottom: 1px solid var(--border);
          background: var(--surface-3);
          white-space: nowrap;
        }

        thead th.r {
          text-align: right;
        }

        thead th:first-child {
          padding-left: 18px;
        }

        thead th:last-child {
          text-align: right;
          padding-right: 18px;
        }

        tbody tr {
          transition: background 0.08s;
          border-bottom: 1px solid var(--border-sub);
          cursor: pointer;
        }

        tbody tr:last-child {
          border-bottom: none;
        }

        tbody tr:hover {
          background: #F8F9FF;
        }

        tbody td {
          padding: 13px 14px;
          font-size: 12.5px;
          vertical-align: middle;
          white-space: nowrap;
        }

        tbody td:first-child {
          padding-left: 18px;
        }

        tbody td:last-child {
          text-align: right;
          padding-right: 18px;
        }

        tbody td.r {
          text-align: right;
        }

        .c-inv-id {
          font-family: 'JetBrains Mono', monospace;
          font-size: 12.5px;
          font-weight: 600;
          color: var(--t1);
        }

        .c-inv-client {
          font-size: 11.5px;
          color: var(--t3);
          margin-top: 2px;
        }

        .c-period {
          font-size: 12.5px;
          color: var(--t2);
        }

        .c-num {
          font-family: 'JetBrains Mono', monospace;
          font-size: 13px;
          font-weight: 500;
          color: var(--t2);
        }

        .c-final {
          font-family: 'JetBrains Mono', monospace;
          font-size: 13.5px;
          font-weight: 700;
          color: var(--blue);
        }

        .c-dash {
          color: var(--t5);
          font-size: 15px;
        }

        .c-date {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11.5px;
          color: var(--t3);
        }

        .status-sel {
          appearance: none;
          border-radius: 99px;
          border: 1.5px solid;
          padding: 4px 22px 4px 10px;
          font-family: inherit;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          outline: none;
          transition: all 0.14s;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 6px center;
        }

        .ss-draft {
          background: var(--surface-2);
          color: #6B7280;
          border-color: #D1D5DB;
        }

        .ss-paid {
          background: var(--green-l);
          color: var(--green-h);
          border-color: var(--green-b);
        }

        .ss-pending {
          background: var(--amber-l);
          color: var(--amber-h);
          border-color: var(--amber-b);
        }

        .ss-overdue {
          background: var(--red-l);
          color: var(--red-h);
          border-color: var(--red-b);
        }

        .act-row {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          justify-content: flex-end;
        }

        .actb {
          width: 28px;
          height: 28px;
          border-radius: 5px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          transition: all 0.12s;
          position: relative;
        }

        .actb svg {
          width: 13px;
          height: 13px;
          color: var(--t4);
        }

        .actb.view:hover {
          background: var(--blue-l);
        }

        .actb.view:hover svg {
          color: var(--blue);
        }

        .actb.regen:hover {
          background: var(--teal-l);
        }

        .actb.regen:hover svg {
          color: var(--teal);
        }

        .actb.override:hover {
          background: var(--green-l);
        }

        .actb.override:hover svg {
          color: var(--green);
        }

        .actb.pdf:hover {
          background: var(--amber-l);
        }

        .actb.pdf:hover svg {
          color: var(--amber);
        }

        .actb.del:hover {
          background: var(--red-l);
        }

        .actb.del:hover svg {
          color: var(--red);
        }

        .actb[data-tip]:hover::after {
          content: attr(data-tip);
          position: absolute;
          bottom: calc(100% + 6px);
          left: 50%;
          transform: translateX(-50%);
          background: var(--t1);
          color: #fff;
          font-size: 10.5px;
          font-weight: 500;
          padding: 3px 8px;
          border-radius: 4px;
          white-space: nowrap;
          pointer-events: none;
          font-family: 'Inter', sans-serif;
          z-index: 50;
        }

        .actb[data-tip]:hover::before {
          content: '';
          position: absolute;
          bottom: calc(100% + 2px);
          left: 50%;
          transform: translateX(-50%);
          border: 4px solid transparent;
          border-top-color: var(--t1);
          pointer-events: none;
          z-index: 50;
        }

        .pag {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          padding: 9px 16px;
          border-top: 1px solid var(--border-sub);
          background: var(--surface-3);
        }

        .pag-info {
          font-size: 12px;
          color: var(--t4);
        }

        .pag-rpp {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--t2);
        }

        .pag-rpp select {
          border: 1px solid var(--border);
          background: var(--surface);
          border-radius: 5px;
          padding: 3px 6px;
          font-family: inherit;
          font-size: 12px;
          outline: none;
          cursor: pointer;
        }

        .pag-btns {
          display: flex;
          gap: 3px;
        }

        .pagb {
          width: 26px;
          height: 26px;
          border-radius: 5px;
          border: 1px solid var(--border);
          background: var(--surface);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.1s;
        }

        .pagb:hover {
          background: var(--surface-2);
        }

        .pagb svg {
          width: 12px;
          height: 12px;
          color: var(--t2);
        }

        .pagb:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .empty-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 72px 24px;
          gap: 12px;
          text-align: center;
        }

        .empty-ico {
          width: 60px;
          height: 60px;
          background: var(--surface-2);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--border);
          margin-bottom: 4px;
        }

        .empty-ico svg {
          width: 26px;
          height: 26px;
          color: var(--t5);
        }

        .empty-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--t1);
          letter-spacing: -0.2px;
        }

        .empty-sub {
          font-size: 13px;
          color: var(--t3);
          max-width: 300px;
          line-height: 1.65;
        }

        .status-pill {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .sp-draft {
          background: #F3F4F6;
          color: #6B7280;
        }

        .sp-paid {
          background: #ECFDF5;
          color: #059669;
        }

        .sp-pending {
          background: #FEF3C7;
          color: #D97706;
        }

        .sp-overdue {
          background: #FEE2E2;
          color: #DC2626;
        }

        .act-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 6px 14px;
          background: #4F46E5;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          white-space: nowrap;
        }

        .act-btn:hover:not(:disabled) {
          background: #4338CA;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }

        .act-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      <div className="invoices-page">
        <SettingsBackButton />
        {/* Page Header */}
        <div className="ph">
          <h1 className="ph-title">Invoices</h1>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={handleExportCSV}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="qstats">
          <div className="qstat">
            <div className="qstat-ico qi-all">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div>
              <div className="qstat-val">{formatInvoiceAmount(stats.total)}</div>
              <div className="qstat-lbl">Total Revenue</div>
            </div>
          </div>
          <div className="qstat">
            <div className="qstat-ico qi-paid">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <div>
              <div className="qstat-val">{formatInvoiceAmount(stats.paid)}</div>
              <div className="qstat-lbl">Paid</div>
            </div>
          </div>
          <div className="qstat">
            <div className="qstat-ico qi-pend">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div>
              <div className="qstat-val">{formatInvoiceAmount(stats.pending)}</div>
              <div className="qstat-lbl">Pending</div>
            </div>
          </div>
          <div className="qstat">
            <div className="qstat-ico qi-over">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div>
              <div className="qstat-val">{formatInvoiceAmount(stats.overdue)}</div>
              <div className="qstat-lbl">Overdue</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filter-card">
          <div className="fsearch-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search invoice, client…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="fsel"
            value={state.selectedMonth}
            onChange={(e) => setState(prev => ({
              ...prev,
              selectedMonth: Number(e.target.value),
              selectedProject: '',
            }))}
          >
            {MONTH_OPTIONS.map(month => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
          <select
            className="fsel"
            value={state.selectedYear}
            onChange={(e) => setState(prev => ({
              ...prev,
              selectedYear: Number(e.target.value),
              selectedProject: '',
            }))}
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select
            className="fsel"
            value={state.selectedClient}
            onChange={(e) => setState(prev => ({
              ...prev,
              selectedClient: e.target.value,
              selectedProject: '',
            }))}
          >
            <option value="">Client</option>
            {clients.map((client: any) => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>
          {state.selectedClient && (
            <select
              className="fsel"
              value={state.selectedProject}
              onChange={(e) => setState(prev => ({ ...prev, selectedProject: e.target.value }))}
            >
              <option value="">Project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.projectName}</option>
              ))}
            </select>
          )}
          <select
            className="fsel"
            value={state.selectedStatus}
            onChange={(e) => setState(prev => ({ ...prev, selectedStatus: e.target.value as InvoiceStatus | '' }))}
          >
            <option value="">Status</option>
            {INVOICE_STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <div className="fsp"></div>
          <button className={`refresh-btn ${refreshing ? 'spinning' : ''}`} onClick={handleRefresh}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Refresh
          </button>
        </div>

        {/* Table */}
        <div className="table-card">
          <div className="table-toolbar">
            <span className="tcnt">
              Showing <b>{paginatedRows.length}</b> of <b>{filteredRows.length}</b> invoices
            </span>
          </div>
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: '170px' }}>Invoice # / Client</th>
                  <th>Period</th>
                  <th className="r">Placements</th>
                  <th className="r">Total Amount</th>
                  <th className="r">Override</th>
                  <th className="r">Final Amount</th>
                  <th>Status</th>
                  <th>Generated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(clientsLoading || invoicesLoading) ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                      Loading...
                    </td>
                  </tr>
                ) : paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 0 }}>
                      <div className="empty-wrap">
                        <div className="empty-ico">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                          </svg>
                        </div>
                        <div className="empty-title">No invoices found</div>
                        <div className="empty-sub">No invoices match your current filters. Try adjusting the month, client, or status.</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map(row => row.type === 'invoice' && row.invoice ? (
                    <tr key={row.invoice.id} onClick={() => handleOpenDetailsDialog(row.invoice!)}>
                      <td>
                        <div className="c-inv-id">{row.invoice.invoiceNumber}</div>
                        <div className="c-inv-client">{row.invoice.client?.name}</div>
                      </td>
                      <td>
                        <span className="c-period">{getMonthName(row.invoice.month)} {row.invoice.year}</span>
                      </td>
                      <td className="r">
                        <span className="c-num">{row.invoice.items?.length || 0}</span>
                      </td>
                      <td className="r">
                        <span className="c-num">{formatInvoiceAmount(row.invoice.totalAmount)}</span>
                      </td>
                      <td className="r">
                        {row.invoice.overrideAmount ? (
                          <span className="c-num" style={{ color: 'var(--amber)' }}>
                            {formatInvoiceAmount(row.invoice.overrideAmount)}
                          </span>
                        ) : (
                          <span className="c-dash">—</span>
                        )}
                      </td>
                      <td className="r">
                        <span className="c-final">{formatInvoiceAmount(row.invoice.finalAmount)}</span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {canEditInvoices ? (
                          <select
                            className={`status-sel ${getStatusClass(row.invoice.status)}`}
                            value={row.invoice.status}
                            onChange={(e) => handleStatusChange(row.invoice!, e.target.value as InvoiceStatus)}
                            disabled={updateStatusMutation.isPending}
                          >
                            {INVOICE_STATUS_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`sp-${row.invoice.status.toLowerCase()}`}>{row.invoice.status}</span>
                        )}
                      </td>
                      <td>
                        <span className="c-date">{new Date(row.invoice.generatedAt).toLocaleDateString()}</span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="act-row">
                          <button
                            className="actb view"
                            data-tip="View details"
                            onClick={() => handleOpenDetailsDialog(row.invoice!)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                          {canEditInvoices && (
                          <button
                            className="actb regen"
                            data-tip="Regenerate"
                            onClick={() => regenerateMutation.mutate(row.invoice!.id)}
                            disabled={regenerateMutation.isPending}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="23 4 23 10 17 10"/>
                              <polyline points="1 20 1 14 7 14"/>
                              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                            </svg>
                          </button>
                          )}
                          {canEditInvoices && (
                          <button
                            className="actb override"
                            data-tip="Override amount"
                            onClick={() => handleOpenOverrideDialog(row.invoice!)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="12" y1="1" x2="12" y2="23"/>
                              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                            </svg>
                          </button>
                          )}
                          <button
                            className="actb pdf"
                            data-tip="Download PDF"
                            onClick={() => handleDownloadPDF(row.invoice!)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14 2 14 8 20 8"/>
                              <line x1="16" y1="13" x2="8" y2="13"/>
                              <line x1="16" y1="17" x2="8" y2="17"/>
                            </svg>
                          </button>
                          <button
                            className="actb del"
                            data-tip="Delete"
                            onClick={() => handleOpenDeleteDialog(row.invoice!)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14H6L5 6"/>
                              <path d="M10 11v6"/>
                              <path d="M14 11v6"/>
                              <path d="M9 6V4h6v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : null)
                )}
              </tbody>
            </table>
          </div>
          {paginatedRows.length > 0 && (
            <div className="pag">
              <div className="pag-rpp">
                Rows per page:{' '}
                <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <span className="pag-info">
                {page} of {totalPages}
              </span>
              <div className="pag-btns">
                <button className="pagb" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <button className="pagb" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Details Dialog with Inline Editing */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => {
          setDetailsDialogOpen(false);
          setEditingItemId(null);
          setEditingItemPrice('');
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--t1)' }}>
              Invoice Details - {selectedInvoice?.invoiceNumber}
            </span>
            {selectedInvoice && (
              <span className={`status-pill ${getStatusPillClass(selectedInvoice.status)}`}>
                {selectedInvoice.status}
              </span>
            )}
          </div>
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <div style={{ paddingTop: '8px' }}>
              {/* Invoice Header Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--t4)', marginBottom: '4px' }}>Client:</div>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--t1)' }}>
                    {selectedInvoice.client?.name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--t3)', marginTop: '2px' }}>
                    {selectedInvoice.client?.email}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--t4)', marginBottom: '4px' }}>Period:</div>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--t1)' }}>
                    {getMonthName(selectedInvoice.month)} {selectedInvoice.year}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--t3)', marginTop: '8px' }}>
                    Generated: {new Date(selectedInvoice.generatedAt).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Invoice Items Table with Inline Editing */}
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--t1)', marginTop: '20px', marginBottom: '10px' }}>
                Invoice Items{canEditInvoices ? ' (Click edit icon to change price)' : ''}
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 'auto' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-3)' }}>
                      <th style={{ padding: '8px 12px', fontSize: '10px', fontWeight: 600, color: 'var(--t4)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>#</th>
                      <th style={{ padding: '8px 12px', fontSize: '10px', fontWeight: 600, color: 'var(--t4)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</th>
                      <th style={{ padding: '8px 12px', fontSize: '10px', fontWeight: 600, color: 'var(--t4)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Live URL</th>
                      <th style={{ padding: '8px 12px', fontSize: '10px', fontWeight: 600, color: 'var(--t4)', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Unit Price</th>
                      <th style={{ padding: '8px 12px', fontSize: '10px', fontWeight: 600, color: 'var(--t4)', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Qty</th>
                      <th style={{ padding: '8px 12px', fontSize: '10px', fontWeight: 600, color: 'var(--t4)', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Amount</th>
                      {canEditInvoices && (
                      <th style={{ padding: '8px 12px', fontSize: '10px', fontWeight: 600, color: 'var(--t4)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items?.map((item, index) => (
                      <tr key={item.id} style={{ borderTop: '1px solid var(--border-sub)' }}>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--t2)' }}>{index + 1}</td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--t2)' }}>{item.description}</td>
                        <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                          {item.live_url ? (
                            <a href={item.live_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)', textDecoration: 'none' }}>
                              {item.live_url.length > 40 ? item.live_url.substring(0, 40) + '...' : item.live_url}
                            </a>
                          ) : (
                            <span style={{ color: 'var(--t5)' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'right' }}>
                          {editingItemId === item.id ? (
                            <input
                              type="number"
                              value={editingItemPrice}
                              onChange={(e) => setEditingItemPrice(e.target.value)}
                              min="0"
                              step="0.01"
                              autoFocus
                              style={{
                                width: '100px',
                                padding: '4px 8px',
                                border: '1px solid var(--blue)',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontFamily: 'JetBrains Mono, monospace',
                                outline: 'none'
                              }}
                            />
                          ) : (
                            <span style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--t2)' }}>
                              {formatInvoiceAmount(item.unitPrice)}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'right', color: 'var(--t2)' }}>{item.quantity}</td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: 'var(--t1)' }}>
                          {formatInvoiceAmount(item.amount)}
                        </td>
                        {canEditInvoices && (
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          {editingItemId === item.id ? (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleSaveItemPrice(item.id)}
                                disabled={updateItemPriceMutation.isPending}
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: 'var(--green-l)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px' }}>
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              </button>
                              <button
                                onClick={handleCancelEditItem}
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: 'var(--red-l)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px' }}>
                                  <line x1="18" y1="6" x2="6" y2="18"/>
                                  <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleStartEditItem(item.id, item.unitPrice)}
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                                border: 'none',
                                background: 'var(--blue-l)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px' }}>
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                          )}
                        </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Invoice Totals */}
              <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <div style={{ minWidth: '300px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--t2)' }}>Subtotal:</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', color: 'var(--t1)' }}>
                      {formatInvoiceAmount(selectedInvoice.subtotal)}
                    </span>
                  </div>
                  {selectedInvoice.overrideAmount !== null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', color: 'var(--amber)' }}>Override Amount:</span>
                      <span style={{ fontSize: '13px', fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', color: 'var(--amber)' }}>
                        {formatInvoiceAmount(selectedInvoice.overrideAmount)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '2px solid var(--border)' }}>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--t1)' }}>Total:</span>
                    <span style={{ fontSize: '16px', fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: 'var(--blue)' }}>
                      {formatInvoiceAmount(selectedInvoice.total)}
                    </span>
                  </div>
                </div>
              </div>

              {selectedInvoice.overrideReason && (
                <div style={{ marginTop: '16px', padding: '12px', background: 'var(--blue-l)', border: '1px solid var(--blue-b)', borderRadius: 'var(--r)' }}>
                  <div style={{ fontSize: '12px', color: 'var(--t2)' }}>
                    <strong>Override Reason:</strong> {selectedInvoice.overrideReason}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {selectedInvoice && (
            <Button
              variant="contained"
              onClick={() => handleDownloadPDF(selectedInvoice)}
            >
              Download PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Override Amount Dialog */}
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--t1)' }}>Override Invoice Amount</span>
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <div style={{ paddingTop: '16px' }}>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', color: 'var(--t4)' }}>Invoice:</span>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--t1)', marginTop: '4px', fontFamily: 'JetBrains Mono, monospace' }}>
                  {selectedInvoice.invoiceNumber}
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', color: 'var(--t4)' }}>Client:</span>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--t1)', marginTop: '4px' }}>
                  {selectedInvoice.client?.name}
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <span style={{ fontSize: '11px', color: 'var(--t4)' }}>Original Total:</span>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--blue)', marginTop: '4px', fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatInvoiceAmount(selectedInvoice.totalAmount)}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Override Amount</label>
                <input
                  type="number"
                  value={overrideAmount}
                  onChange={(e) => setOverrideAmount(e.target.value)}
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r)',
                    fontSize: '13px',
                    fontFamily: 'JetBrains Mono, monospace',
                    outline: 'none',
                    transition: 'all 0.15s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--blue)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div style={{ fontSize: '11px', color: 'var(--t4)', marginTop: '6px' }}>
                  Enter 0 to remove override and use original amount
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveOverride}
            variant="contained"
            disabled={overrideMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--t1)' }}>Delete Invoice</span>
        </DialogTitle>
        <DialogContent>
          <div style={{ paddingTop: '8px', fontSize: '13px', color: 'var(--t2)', lineHeight: '1.6' }}>
            Are you sure you want to delete invoice <strong style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--t1)' }}>{selectedInvoice?.invoiceNumber}</strong>?
            <div style={{ marginTop: '8px', color: 'var(--red)' }}>This action cannot be undone.</div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteInvoice}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
